// One ordered, non-overlapping URL match over plain text. `raw` is the exact
// text the user typed (used as the visible label); `href` is always an
// http(s) URL (scheme-less matches are normalized to https so anchors and the
// unfurl endpoint receive an absolute URL). Shared by the render-side
// `linkifyUrls` and the composers' first-URL preview extraction.
export type DetectedUrl = { readonly raw: string; readonly href: string; readonly index: number };

// Curated set of TLDs a *scheme-less* bare domain must end in to be treated as
// a URL. Deliberately excludes ccTLDs that collide with common file
// extensions (md/py/sh/rs/pl/…) so "README.md", "main.py", "script.sh" stay
// plain text. Explicit-scheme and www.-prefixed URLs bypass this check. If this
// proves too narrow, swap the whole module for `linkify-it` behind this same
// interface — that is why detection lives behind `findUrls`.
const COMMON_TLDS = new Set([
  'com', 'org', 'net', 'edu', 'gov', 'mil', 'int', 'io', 'co', 'ai', 'app',
  'dev', 'info', 'biz', 'xyz', 'tech', 'online', 'site', 'store', 'blog',
  'news', 'tv', 'me', 'us', 'uk', 'ca', 'au', 'de', 'fr', 'nl', 'es', 'it',
  'se', 'no', 'fi', 'ie', 'be', 'ch', 'eu', 'in', 'jp', 'br', 'mx', 'nz',
  'ly', 'to', 'gl',
]);

// Candidate spans: an explicit http(s) URL, OR a www./bare host with optional
// port/path/query/fragment. The bare-domain alternative must not start
// mid-token — without a boundary, "a@b.com" in "mailto:a@b.com" matches
// "b.com" as a bare domain (the domain pattern has no anchor of its own; it
// happily starts right after the "@"). The boundary is a captured prefix
// (`^` or one character that cannot continue a token) rather than a
// lookbehind: a lookbehind literal fails to parse on Safari < 16.4, and
// this module is in the post-renderer's import graph, so a parse failure
// here takes the whole bundle down. `findUrls` strips the prefix from the
// reported span. Consuming it is safe: no candidate can begin with a
// character the prefix class admits, so it never swallows the start of the
// next match. Validation (TLD check for the bare branch) and
// trailing-punctuation trimming happen after the match.
const CANDIDATE_RE =
  /(https?:\/\/[^\s<>"'{}|\\^`[\]]+)|(^|[^a-z0-9@])((?:www\.)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:\/[^\s<>"'{}|\\^`[\]]*)?)/gi;

// Strip trailing sentence punctuation, and a single unbalanced closing bracket
// (so "(https://a.com/x)" and "example.com." yield clean matches).
function trimTrailing(match: string): string {
  let out = match;
  // Repeatedly drop trailing . , ; : ! ? and a closing )/]/} with no opener.
  for (;;) {
    const last = out[out.length - 1];
    if (last === undefined) break;
    if ('.,;:!?'.includes(last)) { out = out.slice(0, -1); continue; }
    if ((last === ')' && !out.includes('(')) ||
        (last === ']' && !out.includes('[')) ||
        (last === '}' && !out.includes('{'))) { out = out.slice(0, -1); continue; }
    break;
  }
  return out;
}

function tldOf(host: string): string {
  const labels = host.split('.');
  return labels[labels.length - 1]!.toLowerCase();
}

/** Normalize a single token to an http(s) URL, or null. Mirrors the LinkedIn
 *  "prepend https:// when scheme-less" behavior; rejects non-http schemes.
 *  Returns the candidate string as-is (NOT `new URL().toString()`) so the
 *  editor link dialog keeps its exact prior output — no injected trailing
 *  slash, no host lowercasing. */
export function normalizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0 || /\s/.test(trimmed)) return null;
  // Already an explicit http(s) URL: keep as-is (no reformatting).
  if (/^https?:\/\//i.test(trimmed)) {
    try { new URL(trimmed); } catch { return null; }
    return trimmed;
  }
  // An explicit non-http scheme (mailto:, tel:, ftp://, javascript:, data:, …)
  // is rejected, never rewritten. A scheme label contains no dot; a bare
  // host:port like "example.com:8080" DOES, so it is treated as scheme-less and
  // gets https:// prepended — matching findUrls, so dialog and autolink agree.
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(trimmed);
  if (scheme !== null && !scheme[1]!.includes('.')) return null;
  const candidate = `https://${trimmed}`;
  try { new URL(candidate); } catch { return null; }
  return candidate;
}

export function findUrls(text: string): DetectedUrl[] {
  // Cheap reject: a URL needs a dot (domain) or scheme colon.
  if (!text.includes('.') && !text.includes(':')) return [];
  const out: DetectedUrl[] = [];
  CANDIDATE_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = CANDIDATE_RE.exec(text)) !== null) {
    const schemed = m[1];
    const bareOrWww = m[3];
    const rawMatch = schemed ?? bareOrWww!;
    // The bare branch's captured boundary prefix is not part of the span.
    const index = m.index + (schemed === undefined ? m[2]!.length : 0);
    const raw = trimTrailing(rawMatch);
    if (raw.length === 0) continue;

    if (schemed === undefined) {
      // Bare/www branch: validate. www. bypasses the TLD gate; a bare host
      // must end in a curated TLD.
      const isWww = /^www\./i.test(raw);
      const host = raw.replace(/^www\./i, '').split(/[/:?#]/)[0]!;
      if (!isWww && !COMMON_TLDS.has(tldOf(host))) continue;
    }
    const href = schemed !== undefined ? raw : `https://${raw}`;
    // Guard: only emit http(s) hrefs (defense in depth with normalizeUrl).
    if (!/^https?:\/\//i.test(href)) continue;
    out.push({ raw, href, index });
  }
  return out;
}

export function firstUrl(text: string): DetectedUrl | null {
  const all = findUrls(text);
  return all.length > 0 ? all[0]! : null;
}
